/**
 * DesignSkillPanel — upload / inspect / remove a tenant's brand design skill.
 *
 * A design skill is a .zip bundle (brand.json + assets + a rulebook) that makes
 * agent-generated artifacts (presentations / Word docs) match the tenant's brand
 * exactly. Rendered inside the White-Label Editor's form rail.
 */
import { useCallback, useEffect, useState } from 'react';
import { Upload, Button, Tooltip, Popconfirm, Tag, message, Spin, Switch } from 'antd';
import {
  InboxOutlined, InfoCircleOutlined, DeleteOutlined, FileZipOutlined, ReloadOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import { useLanguage } from '@/hooks/useLanguage';
import { blobErrorMessage } from '@/utils/download';
import {
  getDesignSkill, uploadDesignSkill, deleteDesignSkill, fetchDesignAssetUrl,
  getDesignSkillEnabled, setDesignSkillEnabled,
  getDefaultDesignSkill, uploadDefaultDesignSkill, deleteDefaultDesignSkill,
  setDefaultDesignSkillEnabled, fetchDefaultDesignAssetUrl,
  downloadDesignSkill, downloadDefaultDesignSkill,
  type DesignSkillApiBase,
  type DesignSkillManifest,
} from '../services/designSkillService';

const MAX_ZIP_BYTES = 25 * 1024 * 1024;

interface Props {
  tenantId: string;
  /** Which bundle this panel edits for the tenant:
   *  'own'     — the tenant's OWN brand book (used when branding is on);
   *  'default' — the default INHERITED BY ITS SUBTREE, used whenever an
   *              artifact would otherwise render plain. Same panel, different
   *              endpoints; the default's on/off switch flips the manifest's
   *              own enabled flag (a subtree setting), not a feature flag. */
  scope?: 'own' | 'default';
  /** Which API entrance to use. 'super-admin' (default) on the White-Label
   *  Editor; 'group-admin' on the Admin page's System Design tab, where the
   *  mirrored routes scope every call to the caller's subtree. */
  apiBase?: DesignSkillApiBase;
}

export default function DesignSkillPanel({ tenantId, scope = 'own', apiBase = 'super-admin' }: Props) {
  const isDefault = scope === 'default';
  const { t } = useLanguage();
  const [manifest, setManifest] = useState<DesignSkillManifest | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(false);
  // Default scope only: what this tenant ACTUALLY inherits today (possibly an
  // ancestor's bundle). Shown when the tenant defines none of its own, so the
  // page answers "where is my fallback coming from?" without guessing.
  const [inherited, setInherited] = useState<DesignSkillManifest | null>(null);
  const [flagSaving, setFlagSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const load = isDefault
      ? getDefaultDesignSkill(tenantId, apiBase).then(st => { setInherited(st.inherited); return st.own; })
      : getDesignSkill(tenantId, apiBase);
    load
      .then(m => {
        if (cancelled) return;
        setManifest(m);
        // Default scope: enabled lives ON the manifest.
        if (isDefault) setEnabled(!!(m as any)?.enabled);
      })
      .catch(() => { if (!cancelled) setManifest(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    if (!isDefault) {
      getDesignSkillEnabled(tenantId, apiBase)
        .then(on => { if (!cancelled) setEnabled(on); })
        .catch(() => { if (!cancelled) setEnabled(false); });
    }
    return () => { cancelled = true; };
  }, [tenantId, isDefault, apiBase]);

  const toggleEnabled = useCallback(async (checked: boolean) => {
    setFlagSaving(true);
    try {
      const on = isDefault
        ? await setDefaultDesignSkillEnabled(tenantId, checked, apiBase)
        : await setDesignSkillEnabled(tenantId, checked, apiBase);
      setEnabled(on);
      message.success(checked ? t('designSkill.enabledToast') : t('designSkill.disabledToast'));
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('designSkill.toggleError'));
    } finally {
      setFlagSaving(false);
    }
  }, [tenantId, isDefault, apiBase, t]);

  // Lazily fetch image thumbnails (auth'd blobs → object URLs).
  useEffect(() => {
    if (!manifest) { setThumbs({}); return; }
    const images = manifest.asset_index
      .filter(a => a.kind === 'asset' && a.mime.startsWith('image/'))
      .slice(0, 12);
    let cancelled = false;
    const urls: Record<string, string> = {};
    Promise.all(images.map(async a => {
      try {
        urls[a.logical] = isDefault
          ? await fetchDefaultDesignAssetUrl(tenantId, a.logical, apiBase)
          : await fetchDesignAssetUrl(tenantId, a.logical, apiBase);
      } catch { /* skip */ }
    })).then(() => { if (!cancelled) setThumbs(urls); });
    return () => {
      cancelled = true;
      Object.values(urls).forEach(u => URL.revokeObjectURL(u));
    };
  }, [manifest, tenantId, isDefault, apiBase]);

  const doUpload = useCallback(async (file: File) => {
    if (!file.name.toLowerCase().endsWith('.zip')) {
      message.error(t('designSkill.notZip')); return;
    }
    if (file.size > MAX_ZIP_BYTES) {
      message.error(t('designSkill.tooLarge')); return;
    }
    setUploading(true);
    try {
      const m = isDefault
        ? await uploadDefaultDesignSkill(tenantId, file, apiBase)
        : await uploadDesignSkill(tenantId, file, apiBase);
      setManifest(m);
      if (isDefault) setEnabled(!!(m as any)?.enabled);
      message.success(t('designSkill.uploaded'));
    } catch (e: any) {
      message.error(e?.response?.data?.error || t('designSkill.uploadError'));
    } finally {
      setUploading(false);
    }
  }, [tenantId, isDefault, apiBase, t]);

  const doDownload = useCallback(async () => {
    setDownloading(true);
    try {
      if (isDefault) await downloadDefaultDesignSkill(tenantId, apiBase);
      else await downloadDesignSkill(tenantId, apiBase);
    } catch (e: any) {
      message.error(await blobErrorMessage(e) || t('designSkill.downloadError'));
    } finally {
      setDownloading(false);
    }
  }, [tenantId, isDefault, apiBase, t]);

  const doRemove = useCallback(async () => {
    try {
      if (isDefault) await deleteDefaultDesignSkill(tenantId, apiBase);
      else await deleteDesignSkill(tenantId, apiBase);
      setManifest(null);
      message.success(t('designSkill.removed'));
    } catch {
      message.error(t('designSkill.removeError'));
    }
  }, [tenantId, isDefault, apiBase, t]);

  const families = (manifest?.brand_spec?.colors?.families ?? {}) as Record<string, any>;
  const fonts = (manifest?.brand_spec?.fonts ?? {}) as Record<string, any>;
  const fontList = ['heading', 'body', 'hebrew']
    .map(slot => fonts?.[slot]?.family)
    .filter(Boolean) as string[];

  const infoTooltip = (
    <div style={{ maxWidth: 360, whiteSpace: 'pre-wrap', fontSize: 12 }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('designSkill.formatTitle')}</div>
      {t('designSkill.formatInfo')}
      <div style={{ fontWeight: 600, margin: '8px 0 4px' }}>{t('designSkill.authoringTitle')}</div>
      <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11 }}>
        {t('designSkill.authoringPrompt')}
      </div>
    </div>
  );

  return (
    <section className="wl-section">
      <header className="wl-section-header">
        <h4 className="wl-section-title">
          {t(isDefault ? 'designSkill.defaultTitle' : 'designSkill.title')}{' '}
          <Tooltip title={infoTooltip} overlayStyle={{ maxWidth: 420 }}>
            <InfoCircleOutlined style={{ color: 'var(--text-tertiary)', cursor: 'help' }} />
          </Tooltip>
        </h4>
        <p className="wl-section-subtitle">{t(isDefault ? 'designSkill.defaultSubtitle' : 'designSkill.subtitle')}</p>
      </header>

      <div className="wl-section-body">
        {/* Master on/off. Own scope: flips modules.design_skills (and its
            parent modules.agent_outputs) for this tenant, so agents actually
            apply the uploaded skill. Default scope: flips this bundle's own
            enabled flag for the subtree — and is HIDDEN when the tenant has
            no default of its own, because an off-looking switch beside an
            "inheriting from …" note reads as a contradiction when in fact
            there is simply nothing here to switch. */}
        {(!isDefault || !!manifest) && (
        <div
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 12, padding: '10px 12px', marginBottom: 12, borderRadius: 8,
            border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
            background: 'var(--surface-secondary, #f7f8fa)',
          }}
        >
          <div>
            <div style={{ fontWeight: 600, fontSize: 13 }}>
              {t(isDefault ? 'designSkill.defaultEnableLabel' : 'designSkill.enableLabel')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {t(isDefault ? 'designSkill.defaultEnableHint' : 'designSkill.enableHint')}
            </div>
          </div>
          <Switch
            checked={enabled}
            loading={flagSaving}
            onChange={toggleEnabled}
            data-testid="design-skill-enable-toggle"
          />
        </div>
        )}

        {isDefault && !manifest && inherited && (
          <div
            style={{
              padding: '10px 12px', marginBottom: 12, borderRadius: 8, fontSize: 12.5,
              border: '1px solid var(--border-color, rgba(0,0,0,0.08))',
              background: 'var(--surface-secondary, #f7f8fa)',
              color: 'var(--text-secondary)',
            }}
            data-testid="design-skill-inherited"
          >
            {t('designSkill.inheritedFrom')} <strong dir="auto">{inherited.name}</strong>
          </div>
        )}

        {loading ? (
          <Spin />
        ) : !manifest ? (
          <Upload.Dragger
            accept=".zip"
            multiple={false}
            showUploadList={false}
            disabled={uploading}
            beforeUpload={(file) => { doUpload(file as unknown as File); return false; }}
            data-testid="design-skill-dropzone"
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('designSkill.dropHint')}</p>
            <p className="ant-upload-hint">{t('designSkill.dropSub')}</p>
            {uploading && <Spin style={{ marginTop: 12 }} />}
          </Upload.Dragger>
        ) : (
          <div className="wl-design-skill-card" data-testid="design-skill-summary">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <FileZipOutlined style={{ fontSize: 20, color: 'var(--text-secondary)' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }} dir="auto">{manifest.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                  {t('designSkill.revision')} {manifest.version} · {(manifest.size_bytes / 1024).toFixed(0)} KB ·{' '}
                  {manifest.asset_index.length} {t('designSkill.files')}
                  {!manifest.brand_json_present && (
                    <Tag color="warning" style={{ marginInlineStart: 8 }}>
                      {t('designSkill.noBrandJson')}
                    </Tag>
                  )}
                </div>
              </div>
              {/* The zip is rebuilt from what was KEPT at upload, so it is the
                  bundle the renderers actually use — see the tooltip. */}
              <Tooltip title={t('designSkill.downloadHint')}>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  loading={downloading}
                  onClick={doDownload}
                  data-testid="design-skill-download"
                >
                  {t('designSkill.download')}
                </Button>
              </Tooltip>
              <Upload
                accept=".zip"
                showUploadList={false}
                beforeUpload={(file) => { doUpload(file as unknown as File); return false; }}
              >
                <Button size="small" icon={<ReloadOutlined />} loading={uploading}>
                  {t('designSkill.replace')}
                </Button>
              </Upload>
              <Popconfirm
                title={t('designSkill.removeConfirm')}
                onConfirm={doRemove}
                okText={t('common.yes')}
                cancelText={t('common.no')}
              >
                <Button size="small" danger icon={<DeleteOutlined />}>
                  {t('designSkill.remove')}
                </Button>
              </Popconfirm>
            </div>

            {Object.keys(families).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {t('designSkill.detectedColors')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(families).map(([name, fam]) => (
                    <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      {['base', 'accent', 'tint'].map(tone => fam?.[tone] && (
                        <span
                          key={tone}
                          title={`${name}.${tone} ${fam[tone]}`}
                          style={{
                            width: 18, height: 18, borderRadius: 4,
                            background: fam[tone], border: '1px solid rgba(0,0,0,0.1)',
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {fontList.length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {t('designSkill.detectedFonts')}
                </div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {fontList.map(f => <Tag key={f}>{f}</Tag>)}
                </div>
              </div>
            )}

            {Object.keys(thumbs).length > 0 && (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>
                  {t('designSkill.assets')}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {Object.entries(thumbs).map(([logical, url]) => (
                    <img
                      key={logical}
                      src={url}
                      alt={logical}
                      title={logical}
                      style={{
                        width: 56, height: 56, objectFit: 'contain', borderRadius: 6,
                        background: 'var(--surface-secondary, #f5f5f5)', padding: 4,
                        border: '1px solid rgba(0,0,0,0.08)',
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {manifest.ignored.length > 0 && (
              <details style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                <summary style={{ cursor: 'pointer' }}>
                  {t('designSkill.ignoredFiles', { count: manifest.ignored.length })}
                </summary>
                <div style={{ marginTop: 6 }}>{t('designSkill.noCodeNotice')}</div>
                <ul style={{ margin: '6px 0 0', paddingInlineStart: 18 }}>
                  {manifest.ignored.slice(0, 20).map(i => (
                    <li key={i.logical} style={{ fontFamily: 'JetBrains Mono, monospace' }} dir="auto">
                      {i.logical}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
